import { useCallback, useEffect, useRef, useState } from "react";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import type { VerificacaoTurnstileLogin } from "../loginUnificado.types";

// ------------------------------- FUNCTIONS -------------------------------
export default function useTurnstileLogin(): VerificacaoTurnstileLogin {
	// -------------------------------- STATES --------------------------------
	const widgetRef = useRef<TurnstileInstance | undefined>(undefined);
	const tokenRef = useRef<string | null>(null);
	const usadosRef = useRef(new Set<string>());
	const montadoRef = useRef(false);
	const [token, setToken] = useState<string | null>(null);
	const [mensagemErro, setMensagemErro] = useState<string | null>(null);

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montadoRef.current = true;
		return () => {
			montadoRef.current = false;
			tokenRef.current = null;
		};
	}, []);

	// -------------------------------- HANDLERS ------------------------------
	function confirmarToken(valor: string): void {
		if (!montadoRef.current || !valor.trim() || usadosRef.current.has(valor)) return;
		const widget = widgetRef.current;
		if (!widget || widget.isExpired() || widget.getResponse() !== valor) return;
		tokenRef.current = valor;
		setToken(valor);
		setMensagemErro(null);
	}

	function invalidarToken(): void {
		tokenRef.current = null;
		if (!montadoRef.current) return;
		setToken(null);
		setMensagemErro("Verificação de segurança interrompida ou expirada. Tente novamente.");
	}

	function consumirToken(): string | null {
		const atual = tokenRef.current;
		if (!atual) return null;
		const widget = widgetRef.current;
		if (!widget || usadosRef.current.has(atual) || widget.isExpired() || widget.getResponse() !== atual) {
			invalidarToken();
			return null;
		}
		usadosRef.current.add(atual);
		tokenRef.current = null;
		setToken(null);
		return atual;
	}

	// Consumido por efeitos e callbacks de validação do campo SPACE.
	const renovar = useCallback((): void => {
		tokenRef.current = null;
		if (!montadoRef.current) return;
		setToken(null);
		setMensagemErro(null);
		widgetRef.current?.reset();
	}, []);

	function widgetCarregado(): void {
		tokenRef.current = null;
		if (!montadoRef.current) return;
		setToken(null);
		setMensagemErro(null);
	}

	function registrarErroWidget(codigo: string): void {
		invalidarToken();
		if (!montadoRef.current) return;
		const detalhe = /^\d{6}$/.test(codigo) ? ` (código ${codigo})` : "";
		setMensagemErro(`Não foi possível concluir a verificação de segurança${detalhe}. Tente novamente.`);
	}

	// -------------------------------- RETURN --------------------------------
	return {
		token,
		widgetRef,
		mensagemErro,
		confirmarToken,
		invalidarToken,
		registrarErroWidget,
		widgetCarregado,
		consumirToken,
		renovar,
	};
}
